export interface NavEntry {
  to: string;
  label: string;
  /** Glyph kept for the command palette list. */
  icon: string;
  /** Stroke SVG path drawn in the sidebar (24×24 viewBox). */
  svg: string;
  keywords: string;
  tab: string;
  /**
   * primary: main sidebar list · pilotage: the « Pilotage » sidebar group ·
   * hidden: reachable from ⌘K and the account menu, not listed in the sidebar.
   * The sidebar has no « Plus » menu (Pascal's navigation rule, 26/09/2026).
   */
  zone: 'primary' | 'pilotage' | 'hidden';
  /** Sidebar badge: key of /api/nav-counts and the phrase read by assistive tech. */
  badge?: { key: 'pipeline_to_date' | 'invoices_to_follow' | 'data_quality_high'; label: string };
}

const ICON = {
  home: 'M4 11l8-7 8 7v9h-5v-6H9v6H4z',
  week: 'M4 6h16v14H4zM4 10h16M9 3v4M15 3v4M8 14h3',
  pipe: 'M3 5h18l-7 8v6l-4 2v-8z',
  list: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  plan: 'M4 4h16v16H4zM4 9h16M9 9v11M14 9v11',
  cal: 'M5 5h14v15H5zM5 9h14M9 3v4M15 3v4M9 13h2M13 13h2M9 16h2',
  doc: 'M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6',
  users: 'M9 11a3 3 0 100-6 3 3 0 000 6zM3 20c0-3 3-5 6-5s6 2 6 5M17 11a3 3 0 100-6M21 20c0-2.5-1.5-4.3-4-4.9',
  person: 'M12 11a4 4 0 100-8 4 4 0 000 8zM4 21c0-4 4-6 8-6s8 2 8 6',
  euro: 'M17 6.5A7 7 0 107 17.5M4 10h10M4 14h10',
  shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6zM9 12l2 2 4-4',
  plug: 'M9 3v5M15 3v5M6 8h12v3a6 6 0 01-12 0zM12 17v4',
  spark: 'M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M6 18l2.5-2.5M15.5 8.5L18 6',
  mail: 'M4 6h16v12H4zM4 7l8 6 8-6',
  log: 'M6 4h12v16H6zM9 8h6M9 12h6M9 16h4',
  lock: 'M6 11h12v9H6zM9 11V8a3 3 0 016 0v3',
  eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zM12 15a3 3 0 100-6 3 3 0 000 6z',
};

export const NAV_ENTRIES: NavEntry[] = [
  { to: '/dashboard', label: 'Tableau de bord', icon: '◫', svg: ICON.home, keywords: 'accueil home kpi dashboard semaine mois annee', tab: 'dashboard', zone: 'primary' },
  { to: '/ma-semaine', label: 'Ma semaine', icon: '◷', svg: ICON.week, keywords: 'lundi brief semaine hebdo priorites', tab: 'my_week', zone: 'primary' },
  { to: '/pipeline', label: 'Pipeline', icon: '⇢', svg: ICON.pipe, keywords: 'opportunites prospects affaires hubspot', tab: 'opportunites', zone: 'primary', badge: { key: 'pipeline_to_date', label: 'opportunités sans prochaine action' } },
  { to: '/missions', label: 'Missions', icon: '▤', svg: ICON.list, keywords: 'missions actives contrats', tab: 'missions', zone: 'primary' },
  { to: '/affectation', label: 'Planification', icon: '▦', svg: ICON.plan, keywords: 'production affectation planning repartition', tab: 'production', zone: 'primary' },
  { to: '/calendrier', label: 'Calendrier', icon: '◰', svg: ICON.cal, keywords: 'agenda calendar sessions', tab: 'calendrier', zone: 'primary' },
  { to: '/factures', label: 'Factures', icon: '▭', svg: ICON.doc, keywords: 'invoices facturation billing qonto', tab: 'factures', zone: 'primary', badge: { key: 'invoices_to_follow', label: 'créances à suivre' } },
  { to: '/clients', label: 'Clients', icon: '◎', svg: ICON.users, keywords: 'comptes accounts', tab: 'clients', zone: 'primary' },
  { to: '/intervenants', label: 'Intervenants', icon: '◉', svg: ICON.person, keywords: 'soustraitants consultants freelance', tab: 'soustraitants', zone: 'primary' },
  { to: '/finance-ops', label: 'Finance', icon: '€', svg: ICON.euro, keywords: 'cash tresorerie marge margin', tab: 'finance_ops', zone: 'pilotage' },
  { to: '/data-quality', label: 'Qualité des données', icon: '⎋', svg: ICON.shield, keywords: 'data quality anomalies issues qualite donnees', tab: 'data_quality', zone: 'pilotage', badge: { key: 'data_quality_high', label: 'signalements critiques' } },
  { to: '/integrations', label: 'Intégrations', icon: '⇄', svg: ICON.plug, keywords: 'connecteurs sync fibery hubspot digiforma qonto mcp integrations', tab: 'integrations', zone: 'pilotage' },
  // Hidden routes — reachable from ⌘K and the account menu, not listed in the sidebar
  { to: '/pack-comptable', label: 'Pack comptable', icon: '✉', svg: ICON.mail, keywords: 'comptable expert-comptable email mensuel pack qonto frais', tab: 'accountant_pack', zone: 'hidden' },
  { to: '/ai', label: 'Assistant IA', icon: '✦', svg: ICON.spark, keywords: 'intelligence artificielle recommandations chat copilote', tab: 'ai_insights', zone: 'hidden' },
  { to: '/admin/audit', label: "Journal d'audit", icon: '▤', svg: ICON.log, keywords: 'admin audit log journal', tab: 'audit', zone: 'hidden' },
  { to: '/confidentialite', label: 'Confidentialité', icon: '◎', svg: ICON.lock, keywords: 'rgpd gdpr donnees hebergement privacy dpa', tab: 'privacy', zone: 'hidden' },
  { to: '/client-360', label: 'Vue client', icon: '◎', svg: ICON.eye, keywords: 'client 360 compte fiche', tab: 'client360', zone: 'hidden' },
];

export const PRIMARY_ENTRIES = NAV_ENTRIES.filter((e) => e.zone === 'primary');
export const PILOTAGE_ENTRIES = NAV_ENTRIES.filter((e) => e.zone === 'pilotage');
export const ALL_NAVIGABLE = NAV_ENTRIES; // includes hidden for command palette

export function pathToTab(path: string): string {
  // Match longest prefix first
  const sorted = [...NAV_ENTRIES].sort((a, b) => b.to.length - a.to.length);
  for (const entry of sorted) {
    if (path.startsWith(entry.to)) return entry.tab;
  }
  return 'dashboard';
}

export function tabForPath(to: string): string {
  return NAV_ENTRIES.find((e) => e.to === to)?.tab || to.slice(1) || 'dashboard';
}
